import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Sector } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { SectorForm } from "../SectorForm/SectorForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListSectors.scss";

const sectorController = new Sector();

export function ListSectors({ sectorsActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [sectors,       setSectors]       = useState(null);
  const [sectorsFilter, setSectorsFilter] = useState([]);
  const [search,        setSearch]        = useState("");
  const [showModal,     setShowModal]     = useState(false);
  const [selectedItem,  setSelectedItem]  = useState(null);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [confirmMsg,    setConfirmMsg]    = useState("");
  const [isDelete,      setIsDelete]      = useState(false);
  const [rowMenu,       setRowMenu]       = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".sectors-list__row-menu, .sectors-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setSectors(null);
        const res = await sectorController.getSectors(accessToken, sectorsActive);
        setSectors(res);
        setSectorsFilter(res);
      } catch {
        setSectors([]);
        setSectorsFilter([]);
      }
    })();
  }, [sectorsActive, reload]);

  useEffect(() => {
    if (!sectors) return;
    if (!search) { setSectorsFilter(sectors); return; }
    const q = search.toLowerCase();
    setSectorsFilter(
      sectors.filter((s) =>
        s.code?.toLowerCase().includes(q) || s.name?.toLowerCase().includes(q)
      )
    );
  }, [search, sectors]);

  const openCreate = () => { setSelectedItem(null); setShowModal(true); };
  const openEdit   = (s)  => { setSelectedItem(s);   setShowModal(true); };

  const openToggle = (s) => {
    setSelectedItem(s);
    setIsDelete(false);
    setConfirmMsg(`¿${s.active ? t("deactivate") : t("activate")} "${s.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (s) => {
    setSelectedItem(s);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${s.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await sectorController.updateSector(accessToken, selectedItem._id, { active: !selectedItem.active });
      toast.success(selectedItem.active ? t("sector_deactivated") : t("sector_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await sectorController.deleteSector(accessToken, selectedItem._id);
      toast.success(t("sector_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (sectors === null) return <LoadingSpinner />;

  return (
    <div className="sectors-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="sectors-list__card">
        <div className="sectors-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-sector-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {sectorsFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="sectors-list__tbl-scroll">
            <Table className="sectors-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sectorsFilter.map((s) => (
                  <Table.Row key={s._id}>
                    <Table.Cell data-label={t("code")}>{s.code ? s.code.charAt(0).toUpperCase() + s.code.slice(1).toLowerCase() : ""}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{s.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="sectors-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="sectors-list__row-menu">
                        <button type="button" className="sectors-list__row-menu-btn" onClick={(e) => toggleRowMenu(s._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === s._id && ReactDOM.createPortal(
                        <div
                          className="sectors-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(s); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(s); setRowMenu(null); }}>
                            <Icon name={s.active ? "ban" : "check"} />{s.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="sectors-list__row-menu-item--danger" onClick={() => { openDelete(s); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedItem ? `${t("edit")}: ${selectedItem.name}` : t("new_sector")}
        size="small"
        className="sector-modal"
      >
        <SectorForm
          sector={selectedItem}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedItem?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
